import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { formatPrice } from "@/lib/api";

export default function Cart() {
  const { cart, cartTotal, updateCart, removeFromCart } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  return (
    <div className="ja-container py-16 md:py-24" data-testid="cart-page">
      <div className="text-center mb-12">
        <p className="ja-overline">Your Selection</p>
        <h1 className="ja-serif text-5xl md:text-6xl mt-3 tracking-tight">The Bag</h1>
      </div>

      {!user ? (
        <div className="text-center py-20 border-t" style={{ borderColor: "var(--ja-border)" }}>
          <p className="ja-serif text-2xl">Sign in to view your bag.</p>
          <Link to="/login" className="ja-btn mt-8" data-testid="cart-login-cta">Sign In</Link>
        </div>
      ) : cart.length === 0 ? (
        <div className="text-center py-20 border-t" style={{ borderColor: "var(--ja-border)" }}>
          <p className="ja-serif text-2xl">Your bag is empty.</p>
          <Link to="/shop" className="ja-btn mt-8" data-testid="cart-empty-cta">Continue Shopping</Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-8 border-t" style={{ borderColor: "var(--ja-border)" }}>
            {cart.map((it) => (
              <div key={it.product.id} className="grid grid-cols-[100px_1fr_auto] gap-6 py-8 border-b" style={{ borderColor: "var(--ja-border)" }} data-testid={`cart-item-${it.product.id}`}>
                <Link to={`/product/${it.product.id}`} className="block w-[100px] aspect-square overflow-hidden bg-white">
                  <img src={it.product.images?.[0]} alt={it.product.name} className="w-full h-full object-cover" />
                </Link>
                <div>
                  <p className="ja-overline" style={{ color: "var(--ja-muted)" }}>{it.product.category}</p>
                  <Link to={`/product/${it.product.id}`} className="ja-serif text-xl mt-1 block">{it.product.name}</Link>
                  <p className="text-xs mt-2" style={{ color: "var(--ja-muted)" }}>SKU {it.product.sku}</p>
                  <div className="flex items-center gap-4 mt-4">
                    <div className="flex items-center border" style={{ borderColor: "var(--ja-border)" }}>
                      <button onClick={() => updateCart(it.product.id, it.quantity - 1)} className="w-9 h-9" data-testid={`dec-${it.product.id}`}>−</button>
                      <span className="w-9 text-center text-sm">{it.quantity}</span>
                      <button onClick={() => updateCart(it.product.id, it.quantity + 1)} className="w-9 h-9" data-testid={`inc-${it.product.id}`}>+</button>
                    </div>
                    <button onClick={() => removeFromCart(it.product.id)} className="text-xs tracking-[0.2em] uppercase flex items-center gap-1" data-testid={`remove-${it.product.id}`}>
                      <X size={12} /> Remove
                    </button>
                  </div>
                </div>
                <p className="text-right">{formatPrice(it.product.price * it.quantity, it.product.currency)}</p>
              </div>
            ))}
          </div>

          <aside className="md:col-span-4">
            <div className="border p-8" style={{ borderColor: "var(--ja-border)", background: "var(--ja-ivory)" }}>
              <p className="ja-overline mb-6">Order Summary</p>
              <div className="flex justify-between text-sm mb-3"><span>Subtotal</span><span data-testid="cart-subtotal">{formatPrice(cartTotal)}</span></div>
              <div className="flex justify-between text-sm mb-3"><span>Shipping</span><span>Complimentary</span></div>
              <div className="flex justify-between text-base pt-4 border-t mt-4" style={{ borderColor: "var(--ja-border)" }}><span>Total</span><span className="ja-serif text-xl" data-testid="cart-total">{formatPrice(cartTotal)}</span></div>
              <button onClick={() => nav("/checkout")} className="ja-btn w-full mt-8" data-testid="checkout-button">Proceed to Checkout</button>
              <Link to="/shop" className="block text-center text-xs tracking-[0.2em] uppercase mt-4">Continue Shopping</Link>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
